/**
 * Dettaglio ticket V3: banner + header + conversazione + composer, con tutte
 * le modali di lavorazione (prototipo `Centro Ticket.dc.html` L171-L306).
 *
 * Sostituisce `TicketDetail.tsx` nel Centro Ticket admin e, dal Task 13, anche
 * nell'area rappresentante. La logica d'azione e TRAPIANTATA da quel file,
 * blocco per blocco e senza cambi di comportamento: cambiano solo i componenti
 * a cui i dati vengono passati. `TicketDetail.tsx` e stato rimosso col Task 13:
 * i riferimenti `TicketDetail.tsx:NNN` qui sotto sono citazioni di provenienza,
 * leggibili nella storia git.
 *
 * Mappa dei blocchi trapiantati (TicketDetail.tsx → qui):
 *   :106-130 stato locale del composer → stato di questo componente
 *   :134-149 applyMacroVariables        → `insertMacro` (onPickMacro)
 *   :152-174 chiamata AI + 403/503      → `runAi` (onAi)
 *   :177-198 validazione allegati       → `pickFiles` (onPickFiles)
 *   :202-210 handleActionError (409)    → `handleError` / `onActionError`
 *   :212-259 take / reply / addNote     → `doTake` / `send`
 *   :261-274 changeStatus, "Risolvi"    → `doSetStatus`
 *   :276-288 setAssignees + modale      → `doSaveAssignees`
 *   :290-303 transfer + modale          → `doTransfer`
 *   :305-330 merge + candidati + nav.   → `openMergeModal` / `doMerge`
 *   :334-347 setRequester + modale      → `doSetRequester`
 *   :352-371 ignoreSender + esito parz. → `doIgnoreSender`
 *   :373-379 openAttachment via blob    → `openAttachment`
 *   :384-394 canSetRequester/canIgnore  → contesto di banner e barra azioni
 *   :758-807 le 4 modali + ConfirmDialog → in fondo al componente
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Info, Lock } from 'lucide-react';
import { useToast } from '../../../contexts/ToastContext';
import ConfirmDialog from '../../ConfirmDialog';
import AssigneesModal from '../AssigneesModal';
import TransferModal from '../TransferModal';
import { currentCopyIds, transferArgs } from '../copyGroupsModel';
import MergeModal from '../MergeModal';
import SetRequesterModal, { SetRequesterPayload, type CurrentRequester } from '../SetRequesterModal';
import CloseTicketModal from '../CloseTicketModal';
import DeleteTicketModal from '../DeleteTicketModal';
import InviteParticipantModal from '../InviteParticipantModal';
import { inviteToMentionable } from '../inviteModel';
import TicketHeaderCard from './TicketHeaderCard';
import ThreadPanel from './ThreadPanel';
import Composer from './Composer';
import SimilarCases from './SimilarCases';
import { buildBanners } from './bannerModel';
import { buildActionBar, isLimitedViewer, isMine, type S3ActionId } from './actionsModel';
import { mergeThread } from './threadModel';
import {
  activeMentions,
  initialReplyStatusChoice,
  isDraftReplyMode,
  isReplyBlocked,
  replyStatusChoices,
  TRIAGE_ERROR_CODE,
  TRIAGE_REPLY_NOTICE,
} from './composerModel';
import { emailMessageFlags } from './emailText';
import { slaBadge } from './visual';
import { useNarrow } from './useNarrow';
import { useSupportLook } from '../v4/lookContext';
import { classifySla } from '../../../utils/supportSla';
import { applyMacroVariables } from '../../../services/representativeSupportService';
import {
  ACCEPTED_TYPES,
  MAX_FILES,
  MAX_FILE_SIZE,
  STATUS_LABELS,
  type ComposeTarget,
} from '../constants';
import type {
  Assignee,
  Attachment,
  Candidate,
  GroupLite,
  Macro,
  Mentionable,
  MergeCandidate,
  OperatorSupportApi,
  SimilarCard,
  SuggestReplyResult,
  TicketDetail as TicketDetailData,
} from '../../../services/supportApi';
import type { KnowledgeApi } from '../../../services/supportKnowledgeApi';
import type { PresenceViewer } from '../../../hooks/useSupportSocket';

/**
 * Gestione errori comune a tutte le azioni (`TicketDetail.tsx:202-210`). Sul
 * 409 il ticket e cambiato sotto le mani dell'operatore: oltre all'avviso si
 * rilegge lo stato aggiornato. Esportata perche la stessa regola serve alla
 * pagina, che possiede le card del pannello Contesto.
 */
export const handleSupportActionError = async (
  err: any,
  fallback: string,
  toast: { warning: (m: string) => void; error: (m: string) => void },
  onChanged: () => void | Promise<void>
): Promise<void> => {
  const msg = err?.response?.data?.error;
  if (err?.response?.status === 409 && err?.response?.data?.code === TRIAGE_ERROR_CODE) {
    // Ticket finito (o rimasto) nel canale di smistamento: si ricarica e il
    // composer mostra l'avviso con il trasferimento.
    toast.warning(msg || TRIAGE_REPLY_NOTICE);
    await onChanged();
  } else if (err?.response?.status === 409) {
    toast.warning(msg || 'Il ticket è cambiato: ricarico i dati aggiornati');
    await onChanged();
  } else {
    toast.error(msg || fallback);
  }
};

/**
 * Riga al posto del composer su un ticket chiuso (CT-100, decisione utente
 * 2026-09-30). «Chiuso» e definitivo lato backend (`utils/supportTicket.js`
 * `canReopen`: si riapre solo da «Risolto»; `reply`/`addNote` rispondono 409):
 * nessuna voce «Riapri» esiste nel menu ⋯, quindi il testo non la promette.
 */
export const CLOSED_COMPOSER_NOTICE = 'Ticket chiuso — non si può più rispondere né aggiungere note interne.';

export interface SupportTicketViewProps {
  api: OperatorSupportApi;
  ticket: TicketDetailData;
  candidates: Candidate[];
  /** Tutti i canali visibili: la modale trasferimento esclude quello corrente. */
  allGroups: GroupLite[];
  macros: Macro[];
  /** Nome dell'operatore, usato nella variabile {operatore} delle macro. */
  operatorName?: string;
  /** Presence: chi altro sta guardando il ticket. */
  viewers?: PresenceViewer[];
  /** Id dell'operatore corrente: presence e "il ticket e mio". */
  selfId?: string;
  typingName?: string | null;
  onTypingSignal?: (target: ComposeTarget) => void;
  onTypingStop?: (target: ComposeTarget) => void;
  /** Azioni di lavorazione (`supporttickets.handle`). Default true. */
  canHandle?: boolean;
  /** Trasferimento ad altro canale (permesso separato). Default true. */
  canTransfer?: boolean;
  /**
   * Modifica dei messaggi del RICHIEDENTE nel thread pubblico
   * (`supporttickets.edit_requester_messages` / `associativa.supporto.edit_requester_messages`).
   * Default false: chi ha solo `handle` non modifica nulla.
   */
  canEditRequesterMessages?: boolean;
  /** Modifica dei messaggi del CANALE, compresi i propri. Default false. */
  canEditChannelMessages?: boolean;
  /**
   * Chiusura diretta e silenziosa (`supporttickets.close` /
   * `associativa.supporto.close`). Default false.
   */
  canClose?: boolean;
  /**
   * Cambio del richiedente in qualsiasi momento (`supporttickets.change_requester`
   * / `associativa.supporto.change_requester`). Default false.
   */
  canChangeRequester?: boolean;
  /**
   * Eliminazione definitiva (`supporttickets.delete`, Task 24/D). Solo admin:
   * serve anche `api.deleteTicket`. Default false.
   */
  canDelete?: boolean;
  /**
   * Assegnazione a operatori FUORI dal canale (`supporttickets.assign_external`,
   * Task 26). Solo admin: serve anche `api.searchExternalCandidates`. Default false.
   */
  canAssignExternal?: boolean;
  /**
   * Dopo l'eliminazione: la pagina chiude la scheda e rinfresca coda e
   * contatori (il rifetch del ticket darebbe 404). Assente = `onBack`.
   */
  onDeleted?: () => void | Promise<void>;
  /** Rifetch del ticket (e dei candidati) dopo ogni azione. */
  onChanged: () => void | Promise<void>;
  /** Navigazione interna (merge → ticket superstite). */
  onNavigate: (path: string) => void;
  onBack: () => void;
  /**
   * Pannello Contesto gia costruito dalla pagina. Viene reso QUI solo quando
   * sta nel cassetto (`contextMode='drawer'` oppure sotto i 1024px): il
   * cassetto lo apre `ContextPanel` stesso con `open`/`onClose` (ruling T9),
   * qui non esiste un secondo `S3Drawer`. Sopra i 1024px in modalita 'panel'
   * lo rende la pagina, nella terza colonna.
   */
  contextSlot?: React.ReactNode;
  contextMode?: 'panel' | 'drawer';
  /**
   * Apertura del cassetto Contesto: lo stato vive nella pagina, col pannello.
   * `card` dice su cosa aprirlo ("Cambia stato / priorità" → le Proprietà).
   */
  onContextOpen?: (card?: 'properties') => void;
  /**
   * Trasferimento chiesto dal pannello Proprieta (canale gia scelto). Il
   * `nonce` fa riaprire la modale anche sulla stessa destinazione.
   */
  transferRequest?: { groupId: string; nonce: number };
  /**
   * Redesign v4 (T40): trasferimento delegato alla pagina (pannello rapido
   * `QuickTransferSheet` al posto di `TransferModal`). Usato SOLO con la veste
   * v4: la voce «Trasferisci a un gruppo» del menu ⋯ chiama questa callback
   * con destinazione `''`, e un `transferRequest` di Proprieta con il canale
   * scelto (review ondata 3 #12: stessa UI dai due punti). Assente (o veste
   * v3) = modale di sempre.
   */
  onOpenTransfer?: (groupId: string) => void;
  /**
   * Dopo un trasferimento riuscito dalla modale: la pagina ricarica e annuncia
   * l'esito (toast) al posto di `onChanged` + «Ticket trasferito». Serve quando
   * il ticket esce dal perimetro di chi l'ha trasferito (la ricarica darebbe
   * 403/404): la pagina chiude la scheda con «Ticket trasferito a …».
   * Assente = comportamento di sempre.
   */
  onTransferred?: (toGroupId: string) => void | Promise<void>;
  /** Testo del ritorno (v4: «Torna allo smistamento» / «Torna alla coda»). */
  backLabel?: string;
  /** Nome generico dell'autore di una nota priva di `authorName`. */
  noteAuthorFallback?: string;
  /**
   * Base di conoscenza (Task 29). `canRead`: i «Casi simili usati» linkano la
   * scheda; `canManage`: voce di menu «Crea scheda di conoscenza da questo
   * ticket». Assente = nessuna delle due.
   */
  knowledge?: { api: KnowledgeApi; canRead: boolean; canManage: boolean };
}

const SupportTicketView: React.FC<SupportTicketViewProps> = ({
  api,
  ticket,
  candidates,
  allGroups,
  macros,
  operatorName,
  viewers = [],
  selfId,
  typingName = null,
  onTypingSignal,
  onTypingStop,
  canHandle = true,
  canTransfer = true,
  canEditRequesterMessages = false,
  canEditChannelMessages = false,
  canClose = false,
  canChangeRequester = false,
  canDelete = false,
  canAssignExternal = false,
  onDeleted,
  onChanged,
  onNavigate,
  onBack,
  contextSlot,
  contextMode = 'panel',
  onContextOpen,
  transferRequest,
  onOpenTransfer,
  onTransferred,
  backLabel,
  noteAuthorFallback = 'Operatore',
  knowledge,
}) => {
  const toast = useToast();
  const narrow = useNarrow();
  const look = useSupportLook();
  const id = ticket._id;

  // ===== Stato trapiantato (TicketDetail.tsx:106-130) =====
  const [busy, setBusy] = useState<boolean>(false);
  // Due bozze indipendenti: passando da "Risposta" a "Nota interna" il testo
  // gia scritto non deve sparire (parita con i due composer separati di oggi).
  const [replyBody, setReplyBody] = useState<string>('');
  const [noteBody, setNoteBody] = useState<string>('');
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [noteFiles, setNoteFiles] = useState<File[]>([]);
  // Task 48: @menzioni della nota in scrittura e chi si puo menzionare (caricato
  // la prima volta che si entra in Nota interna su questo ticket).
  const [noteMentions, setNoteMentions] = useState<Mentionable[]>([]);
  const [mentionables, setMentionables] = useState<{ ticketId: string; list: Mentionable[] } | null>(null);
  // Parte B: invito dall'elenco @ — la funzione del Composer che inserisce la
  // menzione della persona scelta nel popup (null = popup chiuso).
  const [invitePick, setInvitePick] = useState<((m: Mentionable) => void) | null>(null);
  const [aiUnavailable, setAiUnavailable] = useState<boolean>(false);
  const [aiLoading, setAiLoading] = useState<ComposeTarget | null>(null);
  // `kind: 'summary'` (Task 28): il riassunto si ACCODA alla nota gia scritta
  // invece di sostituirla, e il riquadro ha un titolo suo.
  // `similarCards` (Task 29): casi della base di conoscenza passati all'assistente.
  const [aiProposal, setAiProposal] = useState<{
    target: ComposeTarget;
    text: string;
    kind?: 'summary';
    similarCards?: SimilarCard[];
  } | null>(null);
  // Ticket corrente, letto dalle risposte AI arrivate in ritardo: una proposta
  // chiesta sul ticket precedente non deve comparire su quello nuovo.
  const currentIdRef = useRef<string>(id);
  currentIdRef.current = id;

  const [mode, setMode] = useState<ComposeTarget>('reply');
  const [publicOnly, setPublicOnly] = useState<boolean>(false);
  // Stato dopo la risposta, scelto nel popup di conferma: `null` = non ancora
  // scelto (conferma disattivata), '' = lascia invariato.
  // `replyStatusAt` è lo stato del ticket quando la scelta è stata fatta (o
  // preselezionata): se il ticket cambia stato col popup aperto, anche una
  // scelta ancora ammessa cambia significato, e si riparte da zero.
  const [replyStatus, setReplyStatus] = useState<string | null>(null);
  const [replyStatusAt, setReplyStatusAt] = useState<string | null>(null);
  const replyStatusNow = replyStatusAt === ticket.status ? replyStatus : null;
  const chooseReplyStatus = (value: string | null) => {
    setReplyStatus(value);
    setReplyStatusAt(ticket.status);
  };
  const replyChoiceId = useId();

  const [assigneeModal, setAssigneeModal] = useState<boolean>(false);
  const [transferModal, setTransferModal] = useState<boolean>(false);
  const [transferInitial, setTransferInitial] = useState<string>('');
  const [mergeModal, setMergeModal] = useState<boolean>(false);
  const [mergeCandidates, setMergeCandidates] = useState<MergeCandidate[]>([]);
  const [requesterModal, setRequesterModal] = useState<boolean>(false);
  const [ignoreSenderConfirm, setIgnoreSenderConfirm] = useState<boolean>(false);
  const [closeModal, setCloseModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  // Conferma prima di una risposta PUBBLICA: capita di credere di scrivere una
  // nota interna e invece si sta per inviare (email compresa) all'associato.
  const [replyConfirm, setReplyConfirm] = useState<boolean>(false);

  const composerRef = useRef<HTMLDivElement>(null);
  /**
   * Richieste di messa a fuoco del composer ("Rispondi"). E un contatore e non
   * un booleano perche l'azione puo ripetersi a modalita invariata, e l'effetto
   * deve rieseguirsi comunque.
   */
  const [focusReq, setFocusReq] = useState<number>(0);

  // Il fuoco si sposta DOPO il render della modalita richiesta. In narrow il
  // composer e sotto il thread scorrevole: senza portarlo in vista la textarea
  // prenderebbe il fuoco fuori dallo schermo.
  useEffect(() => {
    if (focusReq === 0) return;
    const ta = composerRef.current?.querySelector('textarea');
    if (!ta) return;
    ta.focus({ preventScroll: narrow });
    if (narrow) composerRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [focusReq, mode, narrow]);

  // Cambio ticket: le bozze, gli allegati e la proposta AI appartenevano al
  // ticket precedente e non devono seguirci sul successivo.
  useEffect(() => {
    setReplyBody('');
    setNoteBody('');
    setReplyFiles([]);
    setNoteFiles([]);
    setNoteMentions([]);
    setInvitePick(null);
    setAiProposal(null);
    setReplyStatus(null);
    setReplyStatusAt(null);
    setReplyConfirm(false);
    setMode('reply');
    setPublicOnly(false);
  }, [id]);

  // Task 48: elenco dei menzionabili alla prima entrata in Nota interna. Un
  // errore lascia la nota senza elenco (nessun toast: le menzioni sono un di piu).
  const mentionablesFor = mentionables?.ticketId;
  useEffect(() => {
    if (mode !== 'note' || !api.getMentionables || mentionablesFor === id) return;
    const requestId = id;
    setMentionables({ ticketId: requestId, list: [] });
    // Una risposta arrivata dopo il cambio di ticket non deve finire sul nuovo.
    api.getMentionables(requestId).then(
      (list) => {
        if (currentIdRef.current === requestId) {
          setMentionables({ ticketId: requestId, list: Array.isArray(list) ? list : [] });
        }
      },
      () => {}
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  /**
   * Note da evidenziare (Task 23/B). Il backend le calcola alla PRIMA apertura e
   * poi le segna come lette: le riletture successive (dopo un'azione o un evento
   * realtime) arrivano vuote. Si accumulano qui e si azzerano solo cambiando ticket.
   */
  const [newNotes, setNewNotes] = useState<{ ticketId: string; ids: string[] }>(() => ({
    ticketId: id,
    ids: ticket.unreadNoteIds || [],
  }));
  const incomingNoteIds = ticket.unreadNoteIds;
  useEffect(() => {
    setNewNotes((prev) => {
      const incoming = incomingNoteIds || [];
      if (prev.ticketId !== id) return { ticketId: id, ids: incoming };
      const merged = Array.from(new Set([...prev.ids, ...incoming]));
      return merged.length === prev.ids.length ? prev : { ticketId: id, ids: merged };
    });
  }, [id, incomingNoteIds]);
  const highlightedNoteIds = newNotes.ticketId === id ? newNotes.ids : incomingNoteIds || [];

  // Trasferimento chiesto dal pannello Proprieta (destinazione preselezionata).
  const transferNonce = transferRequest?.nonce;
  useEffect(() => {
    if (transferNonce === undefined) return;
    if (look === 'v4' && onOpenTransfer) {
      onOpenTransfer(transferRequest?.groupId || '');
      return;
    }
    setTransferInitial(transferRequest?.groupId || '');
    setTransferModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferNonce]);

  const handleError = useCallback(
    (err: any, fallback: string) => handleSupportActionError(err, fallback, toast, onChanged),
    [toast, onChanged]
  );

  // ===== Macro (TicketDetail.tsx:134-149) =====
  const insertMacro = (macro: Macro, target: ComposeTarget) => {
    // Fallback a stringa vuota: una variabile nota senza valore NON deve
    // lasciare il placeholder grezzo (es. "{nominativo}") nel testo inviato.
    const testo = applyMacroVariables(macro.testo, {
      nominativo: ticket.associateSnapshot?.nominativo || '',
      ticket: '#' + ticket.ticketNumber,
      operatore: operatorName || '',
      canale: ticket.groupId?.nome || '',
    });
    const append = (prev: string) => (prev.trim() ? `${prev}\n${testo}` : testo);
    if (target === 'reply') setReplyBody(append);
    else setNoteBody(append);
  };

  // ===== Assistente AI (TicketDetail.tsx:152-174) =====
  const runAi = async (action: string, target: ComposeTarget) => {
    const text = target === 'reply' ? replyBody : noteBody;
    if (!text.trim()) {
      toast.warning('Scrivi prima del testo da rifinire');
      return;
    }
    setAiLoading(target);
    try {
      // Sulla risposta il ticket viaggia con il testo: nello smistamento il
      // backend rifiuta (409 TICKET_DA_SMISTARE).
      const refined = target === 'reply' ? await api.aiRefine(text, action, id) : await api.aiRefine(text, action);
      setAiProposal({ target, text: refined });
    } catch (err: any) {
      const st = err.response?.status;
      if (st === 409 && err.response?.data?.code === TRIAGE_ERROR_CODE) {
        await handleError(err, 'Errore assistente AI');
      } else if (st === 403 || st === 503) {
        toast.warning('Assistente AI non disponibile');
        setAiUnavailable(true);
      } else {
        toast.error('Errore assistente AI');
      }
    } finally {
      setAiLoading(null);
    }
  };

  // ===== Assistente AI sull'intero ticket (Task 28) =====
  // Errori: 503 = assistente non configurato (sparisce come per la rifinitura);
  // 403/409/429/400/502/504 = messaggio del backend (es. ticket chiuso, troppe
  // richieste, OpenAI in errore o lento).
  const aiTicketError = (err: any) => {
    const st = err?.response?.status;
    const msg = err?.response?.data?.error;
    if (st === 409 && err?.response?.data?.code === TRIAGE_ERROR_CODE) {
      void handleError(err, 'Errore assistente AI');
    } else if (st === 503) {
      toast.warning('Assistente AI non disponibile');
      setAiUnavailable(true);
    } else if ([400, 403, 409, 429, 502, 504].includes(st)) {
      toast.warning(msg || 'Assistente AI non disponibile per questo ticket');
    } else {
      toast.error('Errore assistente AI');
    }
  };

  const runSuggest = async () => {
    const requestId = id;
    setAiLoading('reply');
    try {
      const res: SuggestReplyResult | string = await api.suggestReply(requestId, replyBody.trim() || undefined);
      // Tollerante verso adapter/mock che restituiscono ancora solo il testo.
      const { text, similarCards = [] } = typeof res === 'string' ? { text: res, similarCards: [] } : res;
      if (currentIdRef.current === requestId) setAiProposal({ target: 'reply', text, similarCards });
    } catch (err: any) {
      aiTicketError(err);
    } finally {
      setAiLoading(null);
    }
  };

  // Il riassunto va SEMPRE nella nota interna: dalla modalita risposta si passa
  // alla nota, dove compare la proposta con Usa/Annulla.
  const runSummary = async () => {
    const requestId = id;
    setMode('note');
    setAiLoading('note');
    try {
      const text = await api.summarizeTicket(requestId);
      if (currentIdRef.current === requestId) setAiProposal({ target: 'note', text, kind: 'summary' });
    } catch (err: any) {
      aiTicketError(err);
    } finally {
      setAiLoading(null);
    }
  };

  // ===== Base di conoscenza (Task 29) =====
  // Bozza della scheda dall'intero ticket: resta da approvare nella pagina
  // «Base di conoscenza». 409 = esiste gia una scheda approvata/archiviata,
  // 422 = nessuna risposta utile del canale, 503 = assistente non configurato.
  const doCreateKnowledge = async () => {
    if (!knowledge?.canManage) return;
    setBusy(true);
    try {
      const r = await knowledge.api.generateFromTicket(id);
      toast.success(
        r.created
          ? 'Bozza della scheda creata: approvala nella Base di conoscenza'
          : 'Bozza della scheda aggiornata: approvala nella Base di conoscenza'
      );
    } catch (err: any) {
      const st = err?.response?.status;
      const msg = err?.response?.data?.error;
      if ([400, 403, 409, 422, 429, 502, 503, 504].includes(st)) toast.warning(msg || 'Scheda non creata');
      else toast.error(msg || 'Errore creazione scheda di conoscenza');
    } finally {
      setBusy(false);
    }
  };

  // ===== Allegati (TicketDetail.tsx:177-198) =====
  const validateFiles = useCallback(
    (incoming: File[], existing: File[]): File[] => {
      const accepted: File[] = [];
      for (const f of incoming) {
        if (!ACCEPTED_TYPES.includes(f.type)) {
          toast.error(`Tipo file non consentito: ${f.name}`);
          continue;
        }
        if (f.size > MAX_FILE_SIZE) {
          toast.error(`File troppo grande (max 10MB): ${f.name}`);
          continue;
        }
        accepted.push(f);
      }
      if (existing.length + accepted.length > MAX_FILES) {
        toast.error(`Massimo ${MAX_FILES} allegati`);
        return accepted.slice(0, Math.max(0, MAX_FILES - existing.length));
      }
      return accepted;
    },
    [toast]
  );

  const pickFiles = (picked: File[]) => {
    if (mode === 'note') {
      const ok = validateFiles(picked, noteFiles);
      if (ok.length) setNoteFiles((prev) => [...prev, ...ok]);
    } else {
      const ok = validateFiles(picked, replyFiles);
      if (ok.length) setReplyFiles((prev) => [...prev, ...ok]);
    }
  };

  // ===== Azioni (TicketDetail.tsx:212-379) =====
  const doTake = async () => {
    setBusy(true);
    try {
      await api.take(id);
      await onChanged();
      toast.success('Ticket preso in carico');
    } catch (err: any) {
      await handleError(err, 'Errore presa in carico');
    } finally {
      setBusy(false);
    }
  };

  const doSetStatus = async (status: string) => {
    if (!status) return;
    setBusy(true);
    try {
      await api.setStatus(id, status);
      await onChanged();
      toast.success('Stato aggiornato');
    } catch (err: any) {
      await handleError(err, 'Errore cambio stato');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Invio della bozza corrente: risposta pubblica o nota interna. Per la
   * risposta lo stato successivo si sceglie (obbligatoriamente) nel popup di
   * conferma. Il cambio di stato viaggia DENTRO la stessa chiamata
   * (`thenStatus`): e il backend a scrivere la risposta e a cambiare stato,
   * cosi con "Invia come risolto" il richiedente riceve un solo avviso (la
   * risoluzione) invece di due. Se la transizione non e ammessa il backend
   * risponde 409 senza scrivere nulla e la bozza resta per riprovare.
   */
  const send = async () => {
    // Ctrl+Invio con il popup gia aperto o durante un invio: nulla, cosi la
    // scelta fatta non si azzera e non parte un secondo invio.
    if (busy || replyConfirm) return;
    const isNote = mode === 'note';
    const body = isNote ? noteBody : replyBody;
    const files = isNote ? noteFiles : replyFiles;
    if (!body.trim() && files.length === 0) {
      toast.warning('Scrivi un messaggio o allega un file');
      return;
    }
    // La nota interna parte subito; la risposta pubblica passa dalla conferma,
    // con la scelta dello stato azzerata a ogni apertura.
    if (isNote) return doSend();
    chooseReplyStatus(initialReplyStatusChoice(replyStatusChoices(ticket.status, isDraftReplyMode('reply', ticket))));
    setReplyConfirm(true);
  };

  const doSend = async () => {
    const isNote = mode === 'note';
    const body = isNote ? noteBody : replyBody;
    const files = isNote ? noteFiles : replyFiles;
    const nextStatus = isNote ? '' : replyStatusNow || '';
    setBusy(true);
    try {
      // Task 46: `pending` = la risposta e diventata una bozza da approvare.
      let pending = false;
      if (isNote) {
        // Task 48: solo le menzioni il cui «@Nome» e ancora nel testo.
        const refs = activeMentions(body, noteMentions).map(({ actorType, actorId }) => ({ actorType, actorId }));
        if (refs.length) await api.addNote(id, body.trim(), files, refs);
        else await api.addNote(id, body.trim(), files);
      } else {
        const res = await api.reply(id, body.trim(), files, nextStatus || undefined);
        pending = Boolean(res && res.pending);
      }

      if (isNote) {
        // Parte B: gli invitati di questa nota ora sono partecipanti, menzionabili da subito.
        const invited = activeMentions(body, noteMentions).filter((m) => m.ruolo === 'invitato');
        if (invited.length) {
          setMentionables((prev) => (prev && prev.ticketId === id
            ? { ticketId: id, list: [...prev.list, ...invited.map((m) => ({ ...m, ruolo: 'partecipante' as const }))] }
            : prev));
        }
        setNoteBody('');
        setNoteFiles([]);
        setNoteMentions([]);
      } else {
        setReplyBody('');
        setReplyFiles([]);
      }
      onTypingStop?.(mode);
      setAiProposal((p) => (p && p.target === mode ? null : p));

      await onChanged();
      toast.success(isNote ? 'Nota interna aggiunta' : pending ? 'Bozza inviata per approvazione' : 'Risposta inviata');
      // Bozza: il cambio di stato scelto si applica all'approvazione, non ora.
      if (nextStatus && !pending) {
        toast.success(`Stato aggiornato: ${STATUS_LABELS[nextStatus] || nextStatus}`);
      }
    } catch (err: any) {
      await handleError(err, isNote ? 'Errore aggiunta nota' : 'Errore invio risposta');
    } finally {
      setBusy(false);
    }
  };

  // ===== Risposte in bozza (Task 46) =====
  // Ogni azione rigetta in caso di errore: la card della bozza resta aperta
  // (testo in correzione compreso) e il contenitore avvisa e ricarica.
  const runDraftAction = async (fn: () => Promise<unknown>, okMsg: string, fallback: string) => {
    setBusy(true);
    try {
      await fn();
      await onChanged();
      toast.success(okMsg);
    } catch (err: any) {
      await handleError(err, fallback);
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const doApproveDraft = (draftId: string, body?: string) =>
    api.approveDraft
      ? runDraftAction(
          () => api.approveDraft!(id, draftId, body),
          body !== undefined ? 'Bozza approvata con modifiche: risposta inviata' : 'Bozza approvata: risposta inviata',
          'Errore approvazione bozza'
        )
      : Promise.resolve();

  const doRejectDraft = (draftId: string, note: string) =>
    api.rejectDraft
      ? runDraftAction(() => api.rejectDraft!(id, draftId, note), 'Bozza rifiutata', 'Errore rifiuto bozza')
      : Promise.resolve();

  const doEditDraft = (draftId: string, body: string) =>
    api.editDraft
      ? runDraftAction(() => api.editDraft!(id, draftId, body), 'Bozza modificata', 'Errore modifica bozza')
      : Promise.resolve();

  const doDeleteDraft = (draftId: string) =>
    api.deleteDraft
      ? runDraftAction(() => api.deleteDraft!(id, draftId), 'Bozza ritirata', 'Errore ritiro bozza')
      : Promise.resolve();

  const doSaveAssignees = async (payload: { add: Assignee[]; remove: Assignee[] }) => {
    setBusy(true);
    try {
      await api.setAssignees(id, payload);
      setAssigneeModal(false);
      await onChanged();
      toast.success('Componenti aggiornati');
    } catch (err: any) {
      await handleError(err, 'Errore aggiornamento componenti');
    } finally {
      setBusy(false);
    }
  };

  const doTransfer = async (toGroupId: string, motivo: string, copyGroupIds?: string[]) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.transfer(id, toGroupId, motivo, ...transferArgs(copyGroupIds));
      setTransferModal(false);
      if (onTransferred) {
        await onTransferred(toGroupId);
      } else {
        await onChanged();
        toast.success('Ticket trasferito');
      }
    } catch (err: any) {
      await handleError(err, 'Errore trasferimento');
    } finally {
      setBusy(false);
    }
  };

  const openMergeModal = async () => {
    setBusy(true);
    try {
      const c = await api.getMergeCandidates(id);
      setMergeCandidates(c);
      setMergeModal(true);
    } catch (err: any) {
      await handleError(err, 'Errore caricamento ticket unibili');
    } finally {
      setBusy(false);
    }
  };

  const doMerge = async (targetId: string) => {
    setBusy(true);
    try {
      const survivorId = await api.mergeTicket(id, targetId);
      setMergeModal(false);
      toast.success('Ticket unito');
      onNavigate(api.ticketPath(survivorId));
    } catch (err: any) {
      await handleError(err, 'Errore unione ticket');
    } finally {
      setBusy(false);
    }
  };

  // Attribuzione (needsRequester) o cambio del richiedente (Task 23/C): stessa
  // modale e stessa rotta, il backend decide dal permesso.
  const doSetRequester = async (payload: SetRequesterPayload) => {
    if (!api.setRequester) return;
    const changing = !needsRequester;
    setBusy(true);
    try {
      await api.setRequester(id, payload);
      setRequesterModal(false);
      await onChanged();
      toast.success(
        changing
          ? 'Richiedente cambiato: il nuovo richiedente riceve l’email di apertura'
          : 'Richiedente impostato: le notifiche sono ora attive'
      );
    } catch (err: any) {
      await handleError(err, changing ? 'Errore cambio richiedente' : 'Errore impostazione richiedente');
    } finally {
      setBusy(false);
    }
  };

  // "Non pertinente": il mittente finisce nella lista dei mittenti ignorati e
  // il ticket viene chiuso con tag spam.
  const doIgnoreSender = async () => {
    setIgnoreSenderConfirm(false);
    if (!api.ignoreSender) return;
    setBusy(true);
    try {
      const res = await api.ignoreSender(id);
      await onChanged();
      // `added:false` con reason "lista piena"/"non valido": il ticket e stato
      // comunque chiuso, ma la regola NON c'e e va detto. "gia presente" e un successo.
      if (res && res.added === false && res.reason !== 'gia presente') {
        toast.warning(
          `Mittente non aggiunto (${res.reason || 'lista piena'}). Il ticket è stato chiuso lo stesso.`
        );
      } else {
        toast.success('Mittente ignorato: il ticket è stato chiuso');
      }
    } catch (err: any) {
      await handleError(err, 'Errore esclusione mittente');
    } finally {
      setBusy(false);
    }
  };

  // Chiusura diretta: nessun avviso al richiedente, il motivo resta interno.
  const doClose = async (motivo: string) => {
    if (!api.closeTicket) return;
    setBusy(true);
    try {
      await api.closeTicket(id, motivo || undefined);
      setCloseModal(false);
      await onChanged();
      toast.success('Ticket chiuso');
    } catch (err: any) {
      setCloseModal(false);
      await handleError(err, 'Errore chiusura ticket');
    } finally {
      setBusy(false);
    }
  };

  // Eliminazione definitiva: il ticket non esiste piu, quindi niente
  // `onChanged` (il rifetch darebbe 404): la pagina chiude la scheda.
  const doDelete = async (confirm: string) => {
    if (!api.deleteTicket) return;
    setBusy(true);
    try {
      await api.deleteTicket(id, confirm);
      setDeleteModal(false);
      toast.success('Ticket eliminato');
      await (onDeleted ? onDeleted() : onBack());
    } catch (err: any) {
      setDeleteModal(false);
      await handleError(err, 'Errore eliminazione ticket');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Correzione del testo di un messaggio del thread pubblico. Rigetta sempre in
   * caso di errore: la bolla resta in modifica con la bozza dell'operatore.
   * Il 403 ha un messaggio suo (permesso mancante sul lato del messaggio), il
   * 409 passa da `handleError` che avvisa e ricarica il ticket.
   */
  const doEditMessage = async (messageId: string, body: string) => {
    setBusy(true);
    try {
      await api.editMessage(id, messageId, body);
      await onChanged();
      toast.success('Messaggio modificato');
    } catch (err: any) {
      if (err?.response?.status === 403) {
        toast.error(
          err.response.data?.error || 'Non hai il permesso di modificare questo messaggio'
        );
      } else {
        await handleError(err, 'Errore modifica messaggio');
      }
      throw err;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Rimozione di un allegato da un messaggio (stessi permessi della correzione
   * del testo). Silenziosa: nessuna email. Rigetta in caso di errore.
   */
  const doRemoveAttachment = async (messageId: string, index: number) => {
    if (!api.removeAttachment) return;
    setBusy(true);
    try {
      await api.removeAttachment(id, messageId, index);
      await onChanged();
      toast.success('Allegato rimosso');
    } catch (err: any) {
      if (err?.response?.status === 403) {
        toast.error(
          err.response.data?.error || 'Non hai il permesso di modificare questo messaggio'
        );
      } else {
        await handleError(err, 'Errore rimozione allegato');
      }
      throw err;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Note interne (24/10/2026): correzione ed eliminazione. Chi puo lo dice il
   * backend per ogni nota (`canEdit`/`canDelete`); il 403 (es. finestra dei 15
   * minuti scaduta nel frattempo) ha il messaggio del backend e ricarica il
   * ticket, cosi la matita sparisce. Rigettano in caso di errore.
   */
  const noteError = async (err: any, fallback: string) => {
    if (err?.response?.status === 403) {
      toast.error(err.response.data?.error || 'Non puoi modificare questa nota');
      await onChanged();
    } else {
      await handleError(err, fallback);
    }
  };

  const doEditNote = async (noteId: string, body: string) => {
    if (!api.editNote) return;
    setBusy(true);
    try {
      await api.editNote(id, noteId, body);
      await onChanged();
      toast.success('Nota modificata');
    } catch (err: any) {
      await noteError(err, 'Errore modifica nota');
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const doDeleteNote = async (noteId: string) => {
    if (!api.deleteNote) return;
    setBusy(true);
    try {
      await api.deleteNote(id, noteId);
      await onChanged();
      toast.success('Nota eliminata');
    } catch (err: any) {
      await noteError(err, 'Errore eliminazione nota');
      throw err;
    } finally {
      setBusy(false);
    }
  };

  // Correzione dell'oggetto: nessuna email. Rigetta in caso di errore, cosi
  // l'intestazione resta in modifica con la bozza.
  const doEditSubject = async (subject: string) => {
    if (!api.editSubject) return;
    setBusy(true);
    try {
      await api.editSubject(id, subject);
      await onChanged();
      toast.success('Oggetto modificato');
    } catch (err: any) {
      if (err?.response?.status === 403) {
        toast.error(err.response.data?.error || "Non hai il permesso di modificare l'oggetto");
      } else {
        await handleError(err, 'Errore modifica oggetto');
      }
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const openAttachment = async (a: Attachment) => {
    try {
      await api.openAttachment(id, a.url, a.filename);
    } catch {
      toast.error("Impossibile aprire l'allegato");
    }
  };

  // ===== Contesto delle azioni (TicketDetail.tsx:384-394) =====
  const isAdmin = api.kind === 'admin';
  // `inbound` vale solo sui ticket nati dalla casella di supporto
  // (TicketDetail.tsx:383): altrove non deve abilitare nulla.
  const inbound = ticket.origin === 'email' ? ticket.inbound : undefined;
  const needsRequester = Boolean(inbound?.needsRequester);
  // Prima attribuzione: lato admin con la lavorazione (flusso una-tantum) o con
  // il permesso di cambio; lato rappresentante solo col permesso (la sua rotta
  // lo richiede). Cambio di un richiedente gia attribuito: solo col permesso.
  const canSetRequester =
    Boolean(api.setRequester) && needsRequester && (isAdmin ? canHandle || canChangeRequester : canChangeRequester);
  const canChangeRequesterNow = Boolean(api.setRequester) && canChangeRequester && !needsRequester;
  const currentRequester: CurrentRequester | undefined = ticket.requester
    ? ticket.requester.type === 'associate'
      ? {
          type: 'associate',
          associateId: ticket.requester.associateId,
          associateLabel: ticket.associateSnapshot?.nominativo || ticket.requester.nome,
        }
      : { type: 'guest', email: ticket.requester.email, nome: ticket.requester.nome }
    : undefined;
  // Solo sui ticket arrivati per email (il backend rifiuta gli altri con 409).
  const canIgnoreSender = Boolean(api.ignoreSender) && ticket.origin === 'email' && canHandle;
  const senderEmail = ticket.requester?.email || 'questo mittente';
  const replyRecipient =
    ticket.requester?.nome || ticket.associateSnapshot?.nominativo || ticket.requester?.email || 'chi ha aperto il ticket';
  // La modale trasferimento non propone il canale in cui il ticket gia si trova.
  const transferGroups = allGroups.filter((g) => g._id !== ticket.groupId?._id);

  // Il contesto va nel cassetto quando lo chiede la pagina (area
  // rappresentante: `.assoc-app .page` e largo 1180px, le 3 colonne non ci
  // stanno) oppure sotto i 1024px (ruling 2).
  const contextInDrawer = contextMode === 'drawer' || narrow;

  // Assegnatario esterno al canale (Task 26): menu ridotto e banner. Il backend
  // lo espone come `viewerIsMember`; assente = componente (dati vecchi, mock).
  const isGroupMember = ticket.viewerIsMember !== false;
  const searchExternal =
    canAssignExternal && isGroupMember && api.searchExternalCandidates
      ? (q: string) => api.searchExternalCandidates!(id, q)
      : undefined;
  const externalAssigned = (ticket.assignees || []).filter((a) => a.external);

  // Chi puo attribuire (anche un rappresentante col permesso di cambio) non deve
  // leggere "contatta un amministratore".
  const banners = buildBanners(ticket, { isAdmin: isAdmin || canSetRequester, canSetRequester });
  const bar = buildActionBar({
    status: ticket.status,
    mine: isMine(ticket.assignees, selfId),
    canHandle,
    canTransfer,
    canSetRequester,
    canChangeRequester: canChangeRequesterNow,
    canIgnoreSender,
    canClose: canClose && Boolean(api.closeTicket),
    canDelete: canDelete && isAdmin && Boolean(api.deleteTicket),
    canCreateKnowledge: Boolean(knowledge?.canManage),
    isGroupMember,
    viewerRole: ticket.viewerRole,
    narrow,
    contextInDrawer,
    busy,
  });

  // Senza canHandle le note interne non esistono per l'utente (parita con
  // TicketDetail.tsx:696, dove l'intera colonna e nascosta).
  const items = mergeThread(ticket.publicThread, ticket.internalNotes, {
    publicOnly: publicOnly || !canHandle,
    noteAuthorFallback,
    unreadNoteIds: highlightedNoteIds,
    // Le email in testo semplice arrivano spezzate ogni ~70 caratteri: il
    // thread le mostra riunite (il testo salvato non cambia).
    emailFlags: emailMessageFlags(ticket.publicThread, ticket),
    // Task 46: bozze in attesa (a tutti gli operatori) e rifiutate (solo all'autore).
    // L'utente admin non e mai autore di una bozza.
    pendingReplies: canHandle ? ticket.pendingReplies : undefined,
    selfRepresentativeId: api.kind === 'admin' ? undefined : selfId,
    // Task 48: «Ti ha menzionato» sulle note che citano l'operatore corrente.
    selfActor: selfId ? { actorType: api.kind === 'admin' ? 'user' : 'representative', actorId: selfId } : undefined,
  });
  const noteCount = canHandle ? (ticket.internalNotes || []).filter((n) => n && !n.deletedAt).length : 0;
  // Task 46: la risposta di questo operatore diventa una bozza da approvare.
  const draftReplyMode = isDraftReplyMode('reply', ticket);
  // Scelte del popup di invio. Una scelta fatta su un altro stato del ticket
  // (cambiato mentre il popup è aperto) vale come non fatta.
  const replyChoices = replyStatusChoices(ticket.status, draftReplyMode);
  const replyChoiceOk = replyStatusNow !== null && replyChoices.some((c) => c.value === replyStatusNow);
  // Canale di smistamento: il composer mostra l'avviso con il trasferimento
  // (stesso percorso della voce «Trasferisci a un gruppo» del menu ⋯).
  const replyBlocked = isReplyBlocked(ticket);
  const canTransferNow = canTransfer && isGroupMember && ticket.status !== 'chiuso';

  const onAction = (aid: S3ActionId) => {
    switch (aid) {
      case 'take':
        void doTake();
        break;
      case 'reply':
        // Canale di smistamento: «Rispondi» porta al trasferimento (o, senza
        // permesso, all'avviso al posto della casella di risposta).
        if (replyBlocked) {
          if (canTransferNow) {
            onAction('transfer');
          } else {
            setMode('reply');
            composerRef.current?.scrollIntoView?.({ block: 'nearest' });
          }
          break;
        }
        // Il fuoco NON si sposta qui: `setMode` e asincrono e la textarea da
        // mettere a fuoco e quella della modalita nuova. Ci pensa l'effetto.
        setMode('reply');
        setFocusReq((n) => n + 1);
        break;
      case 'resolve':
        void doSetStatus('risolto');
        break;
      case 'status':
        // "Cambia stato / priorita" apre il cassetto SULLA card Proprieta: e
        // lì che si cambiano, e in un cassetto lungo non e detto sia in vista.
        onContextOpen?.('properties');
        break;
      case 'details':
        onContextOpen?.();
        break;
      case 'transfer':
        if (look === 'v4' && onOpenTransfer) {
          onOpenTransfer('');
          break;
        }
        setTransferInitial('');
        setTransferModal(true);
        break;
      case 'assignees':
        setAssigneeModal(true);
        break;
      case 'merge':
        void openMergeModal();
        break;
      case 'setRequester':
        setRequesterModal(true);
        break;
      case 'ignoreSender':
        setIgnoreSenderConfirm(true);
        break;
      case 'close':
        setCloseModal(true);
        break;
      case 'delete':
        setDeleteModal(true);
        break;
      case 'knowledge':
        void doCreateKnowledge();
        break;
      default:
        break;
    }
  };

  // Parte B: il partecipante invitato (e il canale in copia) legge, scrive note
  // e propone bozze: niente correzione di oggetto/messaggi/allegati né link
  // firmati per «Condividi via email» (il backend risponderebbe 403).
  const limited = isLimitedViewer(ticket.viewerRole);
  const canEditRequesterMsgs = canEditRequesterMessages && !limited;
  const canEditChannelMsgs = canEditChannelMessages && !limited;

  // Parte B: voce «Invita…» nell'elenco @ solo a chi può invitare.
  const canInviteHere = Boolean(ticket.viewerCanInvite) && Boolean(api.searchInvitable);

  return (
    <section className="s3-detail">
      {banners.map((b) => (
        <div key={b.id} className={`s3-card s3-banner s3-banner--${b.tone}`}>
          {b.tone === 'info' ? (
            <Info size={14} className="s3-banner__ico" aria-hidden="true" />
          ) : (
            <AlertTriangle size={14} className="s3-banner__ico" aria-hidden="true" />
          )}
          <div className="s3-banner__body">
            <div className="s3-banner__t">{b.title}</div>
            <div className="s3-banner__d">{b.body}</div>
          </div>
          {b.actionLabel && b.id === 'merged' && ticket.mergedInto && (
            // Resta un link vero: il ticket superstite si deve poter aprire in
            // una scheda nuova, come fa oggi (TicketDetail.tsx:431).
            <Link className="s3-pill s3-pill--sm" to={api.ticketPath(ticket.mergedInto)}>
              {b.actionLabel}
            </Link>
          )}
          {b.actionLabel && b.id === 'needsRequester' && (
            <button
              type="button"
              className="s3-pill s3-pill--sm s3-pill--primary"
              disabled={busy}
              onClick={() => setRequesterModal(true)}
            >
              {b.actionLabel}
            </button>
          )}
        </div>
      ))}

      <TicketHeaderCard
        ticket={ticket}
        bar={bar}
        sla={slaBadge(classifySla(ticket, new Date()))}
        onAction={onAction}
        narrow={narrow}
        onBack={onBack}
        // v4: la scheda e sempre a pagina intera, il ritorno e sempre visibile (CT-47).
        showBack={look === 'v4' || narrow || contextInDrawer}
        backLabel={backLabel}
        viewers={viewers}
        selfId={selfId}
        typingName={typingName}
        busy={busy}
        canEditSubject={(canEditRequesterMsgs || canEditChannelMsgs) && Boolean(api.editSubject)}
        onEditSubject={doEditSubject}
        createShareLinks={
          api.createShareLinks && !limited ? (payload) => api.createShareLinks!(ticket._id, payload) : undefined
        }
        // «Stampa»: come «Condividi via email», non al partecipante invitato ne al
        // canale in copia; note interne solo a chi le vede nella scheda (canHandle).
        print={
          api.printTicket && !limited
            ? { printTicket: (options) => api.printTicket!(ticket._id, options), notesVisible: canHandle }
            : undefined
        }
      />

      <ThreadPanel
        items={items}
        publicOnly={publicOnly}
        onTogglePublicOnly={() => setPublicOnly((v) => !v)}
        noteCount={noteCount}
        canSeeNotes={canHandle}
        onOpenAttachment={openAttachment}
        threadKey={id}
        canEditRequesterMessages={canEditRequesterMsgs}
        canEditChannelMessages={canEditChannelMsgs}
        closed={ticket.status === 'chiuso'}
        onEditMessage={
          canEditRequesterMsgs || canEditChannelMsgs ? doEditMessage : undefined
        }
        onRemoveAttachment={
          (canEditRequesterMsgs || canEditChannelMsgs) && api.removeAttachment
            ? doRemoveAttachment
            : undefined
        }
        onEditNote={api.editNote ? doEditNote : undefined}
        onDeleteNote={api.deleteNote ? doDeleteNote : undefined}
        drafts={
          canHandle
            ? {
                // Nello smistamento l'approvazione sarebbe una risposta all'associato.
                canApprove: Boolean(ticket.viewerCanApprove) && Boolean(api.approveDraft) && !replyBlocked,
                canReply: ticket.viewerCanReply !== false,
                busy,
                onApprove: doApproveDraft,
                onReject: doRejectDraft,
                onEdit: doEditDraft,
                onDelete: doDeleteDraft,
              }
            : undefined
        }
        composer={
          canHandle && ticket.status === 'chiuso' ? (
            // CT-100: su ticket chiuso il composer non si monta (il backend
            // rifiuterebbe comunque risposta e nota). «Risolto» resta com'e.
            <div className={`s3-comp s3-comp--closed${look === 'v4' ? ' s4-comp' : ''}`} role="note">
              {/* support-v3.css non si tocca (v4-common): stile in linea, font piccolo e tenue. */}
              <span
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  font: "500 11.5px 'DM Sans', sans-serif", color: 'var(--a2-fnt)',
                }}
              >
                <Lock size={12} aria-hidden="true" style={{ flex: 'none' }} />
                {CLOSED_COMPOSER_NOTICE}
              </span>
            </div>
          ) : canHandle ? (
            <div ref={composerRef}>
              <Composer
                ticket={ticket}
                mode={mode}
                onModeChange={setMode}
                draft={mode === 'note' ? noteBody : replyBody}
                onDraftChange={(v) => (mode === 'note' ? setNoteBody(v) : setReplyBody(v))}
                files={mode === 'note' ? noteFiles : replyFiles}
                onFilesChange={(f) => (mode === 'note' ? setNoteFiles(f) : setReplyFiles(f))}
                onPickFiles={pickFiles}
                mentionables={mentionables && mentionables.ticketId === id ? mentionables.list : null}
                mentions={noteMentions}
                onMentionsChange={api.getMentionables ? setNoteMentions : undefined}
                onInvite={canInviteHere ? (pick) => setInvitePick(() => pick) : undefined}
                macros={macros}
                onPickMacro={(m) => insertMacro(m, mode)}
                aiUnavailable={aiUnavailable}
                aiLoading={aiLoading === mode}
                onAi={(action) => void runAi(action, mode)}
                onAiSuggest={() => void runSuggest()}
                onAiSummary={() => void runSummary()}
                aiProposalLabel={
                  aiProposal?.kind === 'summary' ? 'Riassunto del ticket (nota interna)' : undefined
                }
                aiProposal={aiProposal?.target === mode ? aiProposal.text : null}
                aiProposalFooter={
                  aiProposal?.target === mode && aiProposal.similarCards?.length ? (
                    <SimilarCases
                      cards={aiProposal.similarCards}
                      cardPath={knowledge?.canRead ? knowledge.api.cardPath : undefined}
                      ticketPath={api.ticketPath}
                    />
                  ) : undefined
                }
                onAiAccept={() => {
                  if (aiProposal) {
                    const { text, target, kind, similarCards } = aiProposal;
                    // Conta l'uso delle schede che hanno ispirato la proposta.
                    if (similarCards?.length && api.markSimilarUsed) {
                      api.markSimilarUsed(similarCards.map((c) => c._id)).catch(() => {});
                    }
                    if (target === 'note') {
                      setNoteBody((prev) => (kind === 'summary' && prev.trim() ? `${prev}\n\n${text}` : text));
                    } else setReplyBody(text);
                  }
                  setAiProposal(null);
                }}
                onAiCancel={() => setAiProposal(null)}
                onSend={() => void send()}
                busy={busy}
                onTypingSignal={onTypingSignal}
                onTypingStop={onTypingStop}
                onTransfer={replyBlocked && canTransferNow ? () => onAction('transfer') : undefined}
              />
            </div>
          ) : undefined
        }
      />

      {/* Il cassetto lo rende `ContextPanel` (open + onClose): qui si rende il
          nodo cosi com'e, senza un secondo S3Drawer. */}
      {contextInDrawer ? contextSlot : null}

      {invitePick && api.searchInvitable && (
        <InviteParticipantModal
          search={(q, page) => api.searchInvitable!(id, q, page)}
          onClose={() => setInvitePick(null)}
          onPick={(c) => {
            const pick = invitePick;
            setInvitePick(null);
            pick(inviteToMentionable(c));
          }}
        />
      )}

      {assigneeModal && (
        <AssigneesModal
          candidates={candidates}
          busy={busy}
          onClose={() => setAssigneeModal(false)}
          onSave={doSaveAssignees}
          searchExternal={searchExternal}
          externalAssigned={externalAssigned}
        />
      )}

      {transferModal && (
        <TransferModal
          groups={transferGroups}
          initialGroupId={transferInitial}
          busy={busy}
          copyOptions={allGroups}
          currentCopies={currentCopyIds(ticket)}
          onClose={() => setTransferModal(false)}
          onTransfer={doTransfer}
        />
      )}

      {requesterModal && (
        <SetRequesterModal
          busy={busy}
          mode={needsRequester ? 'set' : 'change'}
          current={needsRequester ? undefined : currentRequester}
          searchAssociates={api.searchAssociates}
          /* Campo email VUOTO: l'indirizzo della busta e spesso quello di chi
             ha inoltrato, non del richiedente. Precompilarlo invita a
             confermarlo (TicketDetail.tsx:781-782). */
          onClose={() => setRequesterModal(false)}
          onConfirm={doSetRequester}
        />
      )}

      {closeModal && (
        <CloseTicketModal busy={busy} onClose={() => setCloseModal(false)} onConfirm={doClose} />
      )}

      {deleteModal && (
        <DeleteTicketModal
          ticketNumber={ticket.ticketNumber}
          busy={busy}
          onClose={() => setDeleteModal(false)}
          onConfirm={doDelete}
        />
      )}

      {mergeModal && (
        <MergeModal
          candidates={mergeCandidates}
          busy={busy}
          onClose={() => setMergeModal(false)}
          onMerge={doMerge}
        />
      )}

      <ConfirmDialog
        isOpen={ignoreSenderConfirm}
        onClose={() => setIgnoreSenderConfirm(false)}
        onConfirm={doIgnoreSender}
        title="Ignora questo mittente"
        message={`Il ticket viene chiuso con il tag "spam" e le prossime email da ${senderEmail} non diventeranno più ticket. L'indirizzo resta modificabile nella lista dei mittenti ignorati in Posta supporto.`}
        confirmText="Ignora mittente"
        type="danger"
      />

      <ConfirmDialog
        isOpen={replyConfirm}
        onClose={() => setReplyConfirm(false)}
        onConfirm={() => {
          setReplyConfirm(false);
          void doSend();
        }}
        title={draftReplyMode ? 'Invio per approvazione' : "Invio all'associato"}
        message={
          draftReplyMode
            ? `La risposta per ${replyRecipient} diventa una BOZZA: il responsabile del canale la legge e la approva (o la corregge) prima che parta, anche via email. Lo stato che scegli qui sotto si applica all'approvazione. Se volevi scrivere una nota interna, annulla e scegli "Nota interna".`
            : `Stai per inviare una RISPOSTA PUBBLICA a ${replyRecipient}: la riceve anche via email e non si può richiamare. Scegli qui sotto lo stato del ticket dopo l'invio. Se volevi scrivere una nota interna, annulla e scegli "Nota interna".`
        }
        confirmText={draftReplyMode ? 'Invia per approvazione' : "Invia all'associato"}
        confirmDisabled={busy || !replyChoiceOk}
        cancelText="Annulla"
        type="warning"
      >
        {/* Scelta obbligatoria dello stato (25/10/2026): nessuna preselezione,
            salvo quando la scelta è una sola. */}
        <fieldset className="mt-3">
          <legend className="text-sm font-medium text-gray-900 dark:text-white">Come vuoi inviare la risposta?</legend>
          <div className="mt-2 space-y-1.5">
            {replyChoices.map((c, i) => {
              const inputId = `${replyChoiceId}-${i}`;
              const hintId = `${inputId}-hint`;
              const checked = replyStatusNow === c.value;
              return (
                <div
                  key={c.value || 'invariato'}
                  className={`rounded-md border px-3 py-2 ${
                    checked
                      ? 'border-emerald-500 bg-emerald-50 dark:border-emerald-400 dark:bg-emerald-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <label htmlFor={inputId} className="flex cursor-pointer items-start gap-2 text-sm text-gray-800 dark:text-gray-100">
                    <input
                      id={inputId}
                      type="radio"
                      name={`${replyChoiceId}-stato`}
                      value={c.value}
                      checked={checked}
                      onChange={() => chooseReplyStatus(c.value)}
                      aria-describedby={c.hint ? hintId : undefined}
                      className="mt-0.5 accent-emerald-600"
                    />
                    <span>{c.label}</span>
                  </label>
                  {/* `span` e non `p`: in v4 `modals.css` ingrandisce ogni `p` del popup. */}
                  {c.hint && (
                    <span id={hintId} className="ml-6 mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                      {c.hint}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </fieldset>
      </ConfirmDialog>
    </section>
  );
};

export default SupportTicketView;
